/**
 * RoamerCommunityIntelligenceEngine.ts — Engine #7: the Roamer Community
 * Intelligence Engine™.
 *
 * Collects, verifies, aggregates, and distributes community knowledge.
 * NOT a social platform — a trusted knowledge system:
 *  • Verified members > external reviews > anonymous (tier weights), scaled by
 *    a derived contributor reputation (never a stored number that drifts).
 *  • Every aggregate carries a plain-English explanation — mandatory.
 *  • Participation is strictly OPT-IN; users control anonymity, location
 *    sharing, review visibility, history, export, and deletion.
 *  • Output is consumed by Intelligence, Mission, Score, Local, Workspace,
 *    Journey — this engine never applies its own knowledge anywhere.
 *  • Deterministic: aggregation is a pure function of (contributions, now).
 */

import {
  DEFAULT_PRIVACY,
  type CommunityContribution,
  type CommunityPrivacySettings,
  type ContributionType,
  type ContributorProfile,
  type ContributorTier,
  type LocalInsight,
  type ReputationSignals,
  type TargetIntelligence,
} from './models';
import {
  aggregateLocalInsights,
  aggregateTarget,
  recommendationScore,
  type WeightResolver,
} from './aggregation';
import { buildProfile, contributionWeight, EMPTY_SIGNALS } from './reputation';
import { quotaSafeStorage } from '@/services/storage/QuotaSafeStorage';

const STORAGE_KEY = 'nomad:community-intel:v1';
quotaSafeStorage.registerManagedKey(STORAGE_KEY, 'Community Intelligence Engine');
const MAX_CONTRIBUTIONS = 5000;

interface PersistedState {
  contributions: CommunityContribution[];
  contributors: Array<{ id: string; tier: ContributorTier; displayName: string; signals: ReputationSignals; joinedAt: number }>;
  privacy: CommunityPrivacySettings;
}

export interface ContributionInput {
  type: ContributionType;
  targetId: string;
  targetLabel: string;
  locationId: string;
  rating?: number | null;
  attributes?: Record<string, string | number | boolean>;
  text?: string | null;
  at?: number;
}

type ChangeListener = () => void;

// ── Persisted-state sanitizers (corrupt records are dropped, never trusted) ───

const CONTRIBUTION_TYPES: ReadonlySet<string> = new Set<ContributionType>([
  'cafeReview', 'coworkingReview', 'apartmentReview', 'neighborhoodReview',
  'internetSpeedReport', 'noiseLevelReport', 'powerReliabilityReport', 'safetyReport',
  'transitTip', 'localAdvice', 'scamWarning', 'visaExperience', 'healthcareExperience',
  'costReport', 'favoriteLocation', 'hiddenGem', 'photographySpot', 'weekendActivity',
  'monthlyBudgetReport',
]);
const TIERS: ReadonlySet<string> = new Set<ContributorTier>(['verified', 'anonymous', 'external']);

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function finiteNumber(v: unknown, fallback: number): number {
  return typeof v === 'number' && Number.isFinite(v) ? v : fallback;
}

function sanitizeContribution(raw: unknown): CommunityContribution | null {
  if (!isRecord(raw)) return null;
  if (
    typeof raw.id !== 'string' ||
    typeof raw.targetId !== 'string' ||
    typeof raw.contributorId !== 'string' ||
    typeof raw.locationId !== 'string' ||
    typeof raw.type !== 'string' || !CONTRIBUTION_TYPES.has(raw.type) ||
    typeof raw.tier !== 'string' || !TIERS.has(raw.tier) ||
    typeof raw.at !== 'number' || !Number.isFinite(raw.at)
  ) {
    return null;
  }
  const attributes: Record<string, string | number | boolean> = {};
  if (isRecord(raw.attributes)) {
    for (const [k, v] of Object.entries(raw.attributes)) {
      if (typeof v === 'string' || typeof v === 'boolean' || (typeof v === 'number' && Number.isFinite(v))) {
        attributes[k] = v;
      }
    }
  }
  return {
    id: raw.id,
    type: raw.type as ContributionType,
    targetId: raw.targetId,
    targetLabel: typeof raw.targetLabel === 'string' ? raw.targetLabel : raw.targetId,
    locationId: raw.locationId,
    contributorId: raw.contributorId,
    tier: raw.tier as ContributorTier,
    rating: typeof raw.rating === 'number' && Number.isFinite(raw.rating)
      ? Math.max(1, Math.min(5, raw.rating))
      : null,
    attributes,
    text: typeof raw.text === 'string' ? raw.text : null,
    helpfulVotes: Math.max(0, finiteNumber(raw.helpfulVotes, 0)),
    inaccurateReports: Math.max(0, finiteNumber(raw.inaccurateReports, 0)),
    visibility: raw.visibility === 'private' ? 'private' : 'public',
    at: raw.at,
  };
}

function sanitizeContributor(raw: unknown): ContributorProfile | null {
  if (!isRecord(raw) || typeof raw.id !== 'string') return null;
  const tier: ContributorTier =
    typeof raw.tier === 'string' && TIERS.has(raw.tier) ? (raw.tier as ContributorTier) : 'anonymous';
  const s = isRecord(raw.signals) ? raw.signals : {};
  const clamp01 = (v: unknown, fallback: number) => Math.max(0, Math.min(1, finiteNumber(v, fallback)));
  const signals: ReputationSignals = {
    helpfulVotes: Math.max(0, finiteNumber(s.helpfulVotes, 0)),
    verifiedCheckIns: Math.max(0, finiteNumber(s.verifiedCheckIns, 0)),
    missionsCompleted: Math.max(0, finiteNumber(s.missionsCompleted, 0)),
    travelExperience: Math.max(0, finiteNumber(s.travelExperience, 0)),
    contributionQuality: clamp01(s.contributionQuality, 0),
    communityTrust: clamp01(s.communityTrust, 0),
    reportAccuracy: clamp01(s.reportAccuracy, 1),
  };
  return buildProfile(
    raw.id,
    tier,
    typeof raw.displayName === 'string' ? raw.displayName : raw.id,
    signals,
    finiteNumber(raw.joinedAt, 0),
  );
}

function sanitizePrivacy(raw: unknown): CommunityPrivacySettings {
  if (!isRecord(raw)) return { ...DEFAULT_PRIVACY };
  return {
    participationEnabled: raw.participationEnabled === true, // opt-in stays strict
    publicProfile: raw.publicProfile === true,
    anonymousContributions: raw.anonymousContributions === true,
    locationSharing: raw.locationSharing !== false,
    reviewVisibility: raw.reviewVisibility === 'private' ? 'private' : 'public',
  };
}

export class RoamerCommunityIntelligenceEngine {
  private contributions: CommunityContribution[] = [];
  private contributors = new Map<string, ContributorProfile>();
  private privacy: CommunityPrivacySettings = { ...DEFAULT_PRIVACY };
  private listeners = new Set<ChangeListener>();
  private seq = 0;

  /** The local member's contributor id (community identity, not PII). */
  static readonly SELF_ID = 'self';

  constructor() {
    this.load();
    if (!this.contributors.has(RoamerCommunityIntelligenceEngine.SELF_ID)) {
      this.contributors.set(
        RoamerCommunityIntelligenceEngine.SELF_ID,
        buildProfile(RoamerCommunityIntelligenceEngine.SELF_ID, 'verified', 'You', EMPTY_SIGNALS, 0),
      );
    }
  }

  // ── Collection ──────────────────────────────────────────────────────────────

  /**
   * Record the local member's contribution. Returns null unless the user has
   * OPTED IN to community participation (privacy is never implicit).
   */
  contribute(input: ContributionInput): CommunityContribution | null {
    if (!this.privacy.participationEnabled) return null;
    const at = input.at ?? Date.now();
    const tier: ContributorTier = this.privacy.anonymousContributions ? 'anonymous' : 'verified';
    const attributes = { ...(input.attributes ?? {}) };
    if (!this.privacy.locationSharing) {
      // City-level only: strip precise location facts.
      delete attributes['latitude'];
      delete attributes['longitude'];
      delete attributes['address'];
    }
    return this.ingest({
      id: `cc-${at}-${this.seq++}`,
      type: input.type,
      targetId: input.targetId,
      targetLabel: input.targetLabel,
      locationId: input.locationId,
      contributorId: RoamerCommunityIntelligenceEngine.SELF_ID,
      tier,
      rating: input.rating ?? null,
      attributes,
      text: input.text ?? null,
      helpfulVotes: 0,
      inaccurateReports: 0,
      visibility: this.privacy.reviewVisibility,
      at,
    });
  }

  /**
   * Ingest knowledge from other members / external review sources (arrives via
   * sync or the Fusion Engine — not gated by the LOCAL user's opt-in, which
   * only governs their own participation).
   */
  ingestRemote(
    contribution: Omit<CommunityContribution, 'id' | 'helpfulVotes' | 'inaccurateReports' | 'visibility'> & { id?: string },
  ): CommunityContribution {
    if (!this.contributors.has(contribution.contributorId)) {
      this.contributors.set(
        contribution.contributorId,
        buildProfile(contribution.contributorId, contribution.tier, contribution.contributorId, EMPTY_SIGNALS, contribution.at),
      );
    }
    return this.ingest({
      helpfulVotes: 0,
      inaccurateReports: 0,
      visibility: 'public',
      id: contribution.id ?? `cc-${contribution.at}-${this.seq++}`,
      ...contribution,
    } as CommunityContribution)!;
  }

  private ingest(contribution: CommunityContribution): CommunityContribution {
    this.contributions.push(contribution);
    if (this.contributions.length > MAX_CONTRIBUTIONS) this.contributions.shift();
    this.persist();
    this.notify();
    return contribution;
  }

  // ── Verification & reputation ───────────────────────────────────────────────

  /** Future verification methods plug in here (email, check-in, payment, …). */
  setContributorTier(contributorId: string, tier: ContributorTier): void {
    const existing = this.contributors.get(contributorId);
    if (!existing) return;
    this.contributors.set(contributorId, { ...existing, tier });
    for (const c of this.contributions) {
      if (c.contributorId === contributorId) c.tier = tier;
    }
    this.persist();
    this.notify();
  }

  /** Update raw reputation signals; the score is always re-derived. */
  updateReputationSignals(contributorId: string, patch: Partial<ReputationSignals>): void {
    const existing = this.contributors.get(contributorId);
    if (!existing) return;
    this.contributors.set(
      contributorId,
      buildProfile(existing.id, existing.tier, existing.displayName, { ...existing.signals, ...patch }, existing.joinedAt),
    );
    this.persist();
    this.notify();
  }

  voteHelpful(contributionId: string): void {
    const c = this.contributions.find((x) => x.id === contributionId);
    if (!c) return;
    c.helpfulVotes += 1;
    this.recomputeVoteSignals(c.contributorId);
  }

  reportInaccurate(contributionId: string): void {
    const c = this.contributions.find((x) => x.id === contributionId);
    if (!c) return;
    c.inaccurateReports += 1;
    this.recomputeVoteSignals(c.contributorId);
  }

  /**
   * helpfulVotes and reportAccuracy are recomputed from the actual vote counts
   * on the author's contributions — never incrementally reconstructed.
   * Accuracy uses Laplace smoothing (helpful+1)/(helpful+inaccurate+1): it is
   * 1 with no reports and strictly decreases with each inaccuracy report.
   */
  private recomputeVoteSignals(contributorId: string): void {
    let helpful = 0;
    let inaccurate = 0;
    for (const c of this.contributions) {
      if (c.contributorId !== contributorId) continue;
      helpful += c.helpfulVotes;
      inaccurate += c.inaccurateReports;
    }
    const author = this.contributors.get(contributorId);
    if (author) {
      this.updateReputationSignals(contributorId, {
        helpfulVotes: helpful,
        reportAccuracy: (helpful + 1) / (helpful + inaccurate + 1),
      });
    } else {
      this.persist();
      this.notify();
    }
  }

  getContributor(id: string): ContributorProfile | null {
    const p = this.contributors.get(id);
    return p ? structuredClone(p) : null;
  }

  // ── Distribution (engine output — consumed by other engines) ────────────────

  private weightResolver(): WeightResolver {
    return (c) => {
      const reputation = this.contributors.get(c.contributorId)?.reputation ?? 0;
      return contributionWeight(c.tier, reputation);
    };
  }

  private publicContributions(): CommunityContribution[] {
    return this.contributions.filter((c) => c.visibility === 'public');
  }

  /** Aggregated community intelligence for one target. */
  getTargetIntelligence(targetId: string, now: number = Date.now()): TargetIntelligence | null {
    const all = this.publicContributions();
    const mine = all.filter((c) => c.targetId === targetId);
    if (mine.length === 0) return null;
    const weightOf = this.weightResolver();
    const maxSibling = this.maxSiblingScore(all, mine[0].locationId, weightOf, now);
    return aggregateTarget(targetId, mine, weightOf, now, maxSibling);
  }

  /** All targets in a location, ranked by community recommendation score. */
  getLocationIntelligence(locationId: string, now: number = Date.now()): TargetIntelligence[] {
    const all = this.publicContributions().filter((c) => c.locationId === locationId);
    const weightOf = this.weightResolver();
    const byTarget = new Map<string, CommunityContribution[]>();
    for (const c of all) {
      const list = byTarget.get(c.targetId) ?? [];
      list.push(c);
      byTarget.set(c.targetId, list);
    }
    const maxSibling = this.maxSiblingScore(all, locationId, weightOf, now);
    return Array.from(byTarget.entries())
      .map(([targetId, list]) => aggregateTarget(targetId, list, weightOf, now, maxSibling))
      .sort((a, b) => (b.communityRating ?? 0) * b.contributionCount - (a.communityRating ?? 0) * a.contributionCount);
  }

  /** Local knowledge insights ("Best café before 10 AM") for a location. */
  getLocalInsights(locationId: string): LocalInsight[] {
    return aggregateLocalInsights(this.publicContributions(), this.weightResolver(), locationId);
  }

  private maxSiblingScore(
    all: CommunityContribution[],
    locationId: string,
    weightOf: WeightResolver,
    now: number,
  ): number {
    const byTarget = new Map<string, CommunityContribution[]>();
    for (const c of all) {
      if (c.locationId !== locationId) continue;
      const list = byTarget.get(c.targetId) ?? [];
      list.push(c);
      byTarget.set(c.targetId, list);
    }
    let max = 0;
    for (const list of byTarget.values()) {
      const intel = aggregateTarget(list[0].targetId, list, weightOf, now, Infinity);
      const group = { contributions: list, totalWeight: list.reduce((s, c) => s + weightOf(c), 0), verifiedWeight: 0, verifiedCount: 0 };
      max = Math.max(max, recommendationScore(group, intel.communityRating));
    }
    return max;
  }

  // ── Privacy controls (user always in control) ───────────────────────────────

  getPrivacySettings(): CommunityPrivacySettings {
    return { ...this.privacy };
  }

  updatePrivacySettings(patch: Partial<CommunityPrivacySettings>): void {
    this.privacy = { ...this.privacy, ...patch };
    this.persist();
    this.notify();
  }

  /** The local member's contribution history (their data, always viewable). */
  getMyContributions(): CommunityContribution[] {
    return this.contributions
      .filter((c) => c.contributorId === RoamerCommunityIntelligenceEngine.SELF_ID)
      .map((c) => structuredClone(c));
  }

  /** Export the local member's community data as portable JSON. */
  exportMyData(now: number = Date.now()): string {
    return JSON.stringify(
      {
        exportedAt: now,
        privacy: this.privacy,
        profile: this.getContributor(RoamerCommunityIntelligenceEngine.SELF_ID),
        contributions: this.getMyContributions(),
      },
      null,
      2,
    );
  }

  /** Delete the local member's contributions and reset their profile. */
  deleteMyData(): void {
    this.contributions = this.contributions.filter(
      (c) => c.contributorId !== RoamerCommunityIntelligenceEngine.SELF_ID,
    );
    this.contributors.set(
      RoamerCommunityIntelligenceEngine.SELF_ID,
      buildProfile(RoamerCommunityIntelligenceEngine.SELF_ID, 'verified', 'You', EMPTY_SIGNALS, 0),
    );
    this.persist();
    this.notify();
  }

  // ── Subscription ────────────────────────────────────────────────────────────

  onChange(listener: ChangeListener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private notify(): void {
    this.listeners.forEach((l) => {
      try { l(); } catch { /* broken subscriber never breaks the engine */ }
    });
  }

  // ── Persistence ─────────────────────────────────────────────────────────────

  private persist(): void {
    if (typeof localStorage === 'undefined') return;
    try {
      const state: PersistedState = {
        contributions: this.contributions,
        contributors: Array.from(this.contributors.values()).map((p) => ({
          id: p.id, tier: p.tier, displayName: p.displayName, signals: p.signals, joinedAt: p.joinedAt,
        })),
        privacy: this.privacy,
      };
      // BETA GATE HARDENING™: quota-safe write — sheds oldest contributions
      // first under quota pressure (in-memory state and APIs unchanged).
      quotaSafeStorage.writeJSON(STORAGE_KEY, state, (s, level) => ({
        ...s,
        contributions: s.contributions.slice(-(level === 1 ? 2500 : 1000)),
      }));
    } catch { /* storage unavailable — keep working in memory */ }
  }

  private load(): void {
    if (typeof localStorage === 'undefined') return;
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return;
      const state = JSON.parse(raw) as Partial<PersistedState> | null;
      if (state == null || typeof state !== 'object') return;
      // Sanitize record-by-record: malformed entries are dropped, not trusted.
      this.contributions = (Array.isArray(state.contributions) ? state.contributions : [])
        .map(sanitizeContribution)
        .filter((c): c is CommunityContribution => c !== null);
      for (const p of Array.isArray(state.contributors) ? state.contributors : []) {
        const profile = sanitizeContributor(p);
        if (profile) this.contributors.set(profile.id, profile);
      }
      this.privacy = sanitizePrivacy(state.privacy);
    } catch { /* corrupt state — start fresh rather than crash */ }
  }
}

/** App-wide singleton. */
export const roamerCommunityIntelligenceEngine = new RoamerCommunityIntelligenceEngine();
